import React, { useCallback, useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import styles from './index.module.scss';
// import useModal from 'components/common/Modal/useModal';
import PainterPanel from 'components/Painter/PainterPanel';
import MeasureToolsPanel from 'components/MeasureTools/MeasureToolsPanel';
import { ReaderToolBox } from 'components/ReaderToolBox';
import Toolbox from 'components/Toolbox';
import { useStore, StoreTypes } from 'context';
import * as types from "constants/actionTypes";
// import { useMedia } from 'customHooks/media';
import { useReaderStrategyDecider } from 'customHooks/Strategies/ReaderStrategies';
import { EventBus } from 'events/EventBus';
import { ReaderToolsEvent, PainterEvent } from 'events/EventTypes';
import { ReaderToolType } from 'constants/ReaderTools';
import { useWindowSize } from 'customHooks/windowSize';
import ReaderToolComponents from './ReaderToolComponents';
import { useReaderToolsFactory } from './ReaderToolsFactory';
import { ReaderToolbox, ToolboxCategory, MeasureTools } from 'constants/ReaderTools'
import { useReadUserSettings } from 'customHooks/db';
import { useUpdateUserSettings } from 'customHooks/userSetting';
import Repository from 'repositories/Repository';
import { convertArrayToMap } from 'util/array';
import { Roles } from 'constants/role';
import studentNameIcon from './studentNameIcon.svg';
import { usePaiNote } from 'customHooks/paiNote';
import { WarnDialog } from 'components/GroupInteractiveTool/WarnDialog';
import { interactivityApi } from 'components/GroupInteractiveTool/api';
import { ExtendedContentType } from 'components/ExtendedContent/ExtendedContentContainer';
import { setReaderGAState, fireReaderGA } from 'components/AppService/GAService';
import { PainterType, LineType } from 'constants/painterTypes';

const { ReaderToolBarRepository } = Repository;

// const MobileToolsModal = () => <div></div>;
// const MobileTools = () => {
//   const [, { openModal }] = useModal();
//   return <div className={styles.mobileReaderTools} onClick={openModal} />;
// };
const PanelState = {
  None: 'None',
  Pen: 'Pen',
  Highlighter: 'Highlighter',
  Shape: 'Shape',
  Line: 'Line',
  Expression: 'Expression',
  MeasureTools: 'MeasureTools'
};

const shapeNameMap = {
  [PainterType.Star]: '星形',
  [PainterType.Rectangle]: '方形',
  [PainterType.Circle]: '圓形',
  [PainterType.RightTriangle]: '直角三角形',
  [PainterType.IsoscelesTriangle]: '等腰三角形'
};

const ReaderTools = props => {
  const reducers = useStore();
  const [{ role, userId, isLogin }] = reducers[StoreTypes.user];
  const [{ annotationId }] = reducers[StoreTypes.annotation];
  const [{ readerToolColor, isToolboxShow: isReaderToolboxShow, pageIndex, paiNotePage, isDoublePageMode }] = reducers[StoreTypes.reader];
  const [{ books, bookId, catalog }] = reducers[StoreTypes.books];
  const [{paiNote:{ studentInfo, answers: paiNoteStudentAnswers, teacherId, noteId } }, globalDispatch] = reducers[StoreTypes.global];
  // const device = useMedia();
  const windowSize = useWindowSize();
  const readUserSettings = useReadUserSettings();
  const decider = useReaderStrategyDecider();
  const strategy = decider.getReaderStrategy();
  const [{ painterToolType, painterTool, fillType, lineType }] = reducers[StoreTypes.canvas];
  const book = books.find(book => book.bookId === bookId);
  const { LRFlip } = book || {};
  // GA: 實際的 lineType 存在 painterTool[painterToolType] 裡，頂層 lineType 不會被 reducer 更新
  const actualLineType = painterTool && painterTool[painterToolType] && painterTool[painterToolType].lineType;

  const [panelState, setPanelState] = useState(PanelState.None);

  // GA: 用 ref 存放最新狀態，同步到 GAService 共用
  const gaStateRef = useRef({});
  gaStateRef.current = { panelState, painterToolType, painterTool, books, bookId, userId, isLogin, role, annotationId, pageIndex, catalog, isDoublePageMode, LRFlip, lineType: actualLineType, fillType };
  useEffect(() => {
    setReaderGAState(gaStateRef.current);
  });

  // GA: 共用函數，線段或線條類型改變時發送 GA
  // painterTypeOverride: 當從 onPainterChangeHandler 呼叫時傳入新的 painterType，因為 gaStateRef 尚未更新
  const fireLineToolGA = useCallback(function(lineTypeVal, painterTypeOverride) {
    let s = gaStateRef.current;
    if (s.panelState === PanelState.Pen || s.panelState === PanelState.Highlighter || s.panelState === PanelState.Line) {
      let prefix = s.panelState === PanelState.Highlighter ? '工具列_螢光筆' : '工具列_畫筆';
      let currentPainterType = painterTypeOverride;
      if (!currentPainterType) {
        let currentPainterTool = s.painterTool && s.painterTool[s.painterToolType];
        currentPainterType = currentPainterTool && currentPainterTool.painterType;
      }
      let painterModeName = currentPainterType === PainterType.Pen ? '曲線' : currentPainterType === PainterType.Line ? '直線' : '箭頭';
      let lineStyle = lineTypeVal === LineType.Dash ? '虛線' : '實線';
      fireReaderGA(prefix + '_' + painterModeName + '_' + lineStyle);
    }
  }, []);

  // GA: 共用函數，形狀或填充類型改變時發送 GA
  const fireShapeGA = useCallback(function(fillTypeVal, painterTypeOverride) {
    let s = gaStateRef.current;
    if (s.panelState === PanelState.Pen || s.panelState === PanelState.Highlighter || s.panelState === PanelState.Shape) {
      let currentPainterType = painterTypeOverride;
      if (!currentPainterType) {
        let currentPainterTool = s.painterTool && s.painterTool[s.painterToolType];
        currentPainterType = currentPainterTool && currentPainterTool.painterType;
      }
      let shapeName = shapeNameMap[currentPainterType];
      if (shapeName) {
        let prefix = s.panelState === PanelState.Highlighter ? '工具列_螢光筆' : '工具列_畫筆';
        let fillName = fillTypeVal === 'hollow' ? '空心' : '實心';
        fireReaderGA(prefix + '_' + shapeName + '_' + fillName);
      }
    }
  }, []);

  // GA: 監聽 lineType 改變（當用户點擊虛線/實線按钮时）
  const prevLineTypeRef = useRef(actualLineType);
  useEffect(function() {
    if (prevLineTypeRef.current !== actualLineType && (panelState === PanelState.Pen || panelState === PanelState.Highlighter || panelState === PanelState.Line)) {
      fireLineToolGA(actualLineType);
    }
    prevLineTypeRef.current = actualLineType;
  }, [actualLineType, panelState, fireLineToolGA]);

  const [isToolBoxShow, setToolBoxShow] = useState(false);
  const toolBoxCloseHandler = useCallback(() => setToolBoxShow(false), []);

  const readerToolsRef = useRef();
  const ReaderToolsFactory = useReaderToolsFactory();

  const panelCloseHandler = useCallback(() => setPanelState(PanelState.None), []);

  const [leftReaderToolBars, setLeftReaderToolBars] = useState([]);
  const [rightReaderToolBars, setRightReaderToolBars] = useState([]);

  const [toolboxType, setToolboxType] = useState(ToolboxCategory.Settings);
  const [{ readerToolSetting }] = useStore(StoreTypes.settings);
  const updateUserSettings = useUpdateUserSettings();
  const { saveStudentAnswer } = usePaiNote(pageIndex);
  const [openWarnDialog, setOpenWarnDialog] = useState(false);

  useEffect(() => {
    (async () => {
      if (bookId) {
        EventBus.emit({
          event: PainterEvent.LoadReaderToolSettingsEvent,
          payload: { role }
        });
      }
    })();
  }, [bookId, role]);

  useEffect(() => {
    readerToolsRef.current && EventBus.emit({ event: ReaderToolsEvent.SetReaderToolHeight, payload: { readerToolHeight: readerToolsRef.current.offsetHeight } });
  }, [windowSize, readerToolsRef]);

  const setReaderTools = useCallback(readerTools => {
    EventBus.emit({
      event: ReaderToolsEvent.SetReaderToolsEvent,
      payload: {
        readerTools
      }
    });
  }, [])

  useEffect(() => {
    (async () => {
      if (bookId) {
        const left = ReaderToolBarRepository.getLeftReaderTools();
        setLeftReaderToolBars(left);

        const middle = ReaderToolBarRepository.getMiddleReaderTools();
        setReaderTools(
          [
            ...middle.filter(item => !readerToolSetting.includes(item)).map(item => ({ id: item, isActive: false })),
            ...readerToolSetting.map(item => ({ id: item, isActive: true }))
          ]
        );

        const right = await ReaderToolBarRepository.getRightReaderTools(bookId);
        setRightReaderToolBars(right);
      }

    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, setReaderTools]);

  const onColorChange = useCallback((color) => {
    let s = gaStateRef.current;
    if (s.panelState === PanelState.Pen) {
      fireReaderGA('工具列_畫筆_切換顏色');
    } else if (s.panelState === PanelState.Highlighter) {
      fireReaderGA('工具列_螢光筆_切換顏色');
    }
    EventBus.emit({ event: PainterEvent.ChangeBrushColorEvent, payload: { color } });
  }, []);

  const onLineWidthChange = useCallback(lineWidth => {
    let s = gaStateRef.current;
    if (s.panelState === PanelState.Pen) {
      fireReaderGA('工具列_畫筆_切換線條粗細');
    } else if (s.panelState === PanelState.Highlighter) {
      fireReaderGA('工具列_螢光筆_切換線條粗細');
    }
    EventBus.emit({ event: PainterEvent.ChangeBrushWidthEvent, payload: { lineWidth } });
  }, []);

  const onPainterChangeHandler = useCallback(painterType => {
    let s = gaStateRef.current;
    if (shapeNameMap[painterType]) {
      fireShapeGA(s.fillType, painterType);
    } else {
      fireLineToolGA(s.lineType, painterType);
    }
    EventBus.emit({ event: PainterEvent.ChangePainterTypeEvent, payload: { painterType } });
  }, [fireLineToolGA, fireShapeGA]);

  const onBrushChange = useCallback(e => {
    EventBus.emit({ event: PainterEvent.ChangeBrushTypeEvent, payload: { brushType: e.target.value } });
  }, []);

  const onFillTypeChangeHandler = useCallback(function(fillType) {
    fireShapeGA(fillType);
    EventBus.emit({ event: PainterEvent.ChangeShapeFillTypeEvent, payload: { fillType } });
  }, [fireShapeGA]);

  const toolboxTypeChangeHandler = useCallback(toolType => {
    setToolboxType(toolType)
  }, [])

  const onExpressionHandler = useCallback((expressionType) => {
    EventBus.emit({
      event: ReaderToolsEvent.PlayExpression,
      payload: { expressionType }
    });
    setPanelState(PanelState.None)
  }, [])

  const submitNoteHandler = useCallback(async () => {
    const nowPageIndex = paiNotePage && paiNotePage.indexOf(pageIndex);
    if (nowPageIndex < paiNotePage.length - 1) {
      globalDispatch({
        type: types.SET_GROUP_INTERACTIVITY_SNACKBAR,
        payload: {
          message: "請至筆記最後一頁，再進行回傳動作！",
          style: "info",
        },
      });
      return;
    }
    await saveStudentAnswer();
    setOpenWarnDialog(true);
  }, [
    paiNotePage,
    pageIndex,
    saveStudentAnswer,
    globalDispatch,
    setOpenWarnDialog,
  ]);

  const toolboxFilter = useCallback(
    arr => {
      // console.log('arr:',arr)
      return arr.filter(item => ReaderToolbox[toolboxType].includes(item.id) && !item.isActive)},
    [toolboxType])

  const readerToolsFilter = useCallback(arr => {
    return arr.filter(item => item.isActive)
  }, [])

  const measureToolsFilter = arr => arr.filter(item => MeasureTools.includes(item.id) && !item.isActive);

  const resetClickHandler = useCallback(async () => {
    const middle = ReaderToolBarRepository.getMiddleReaderTools();
    const defaultTools = ReaderToolBarRepository.getDefaultReaderTools();

    setReaderTools(
      [
        ...middle.filter(item => !defaultTools.includes(item)).map(item => ({ id: item, isActive: false })),
        ...defaultTools.map(item => ({ id: item, isActive: true }))
      ]
    )

    updateUserSettings({ readerToolSetting: defaultTools }, true);

  }, [setReaderTools, updateUserSettings])

  const onLineTypeChangeHandler = useCallback(function(lineType) {
    EventBus.emit({ event: PainterEvent.ChangelineTypeEvent, payload: { lineType } });
  }, [])


  return (
    <>
    <div id="Reader-Tools" ref={readerToolsRef} style={{ backgroundColor: readerToolColor }}>
      <div
        className={classnames(
          styles.readerTools,
          styles[props.className]
        )}
      >
        {panelState === PanelState.Pen &&
          <PainterPanel.Pen
            toolType={painterToolType}
            painterTool={painterTool[painterToolType]}
            onPanelClose={panelCloseHandler}
            onLineWidthChange={onLineWidthChange}
            onColorChange={onColorChange}
            onPainterHandler={onPainterChangeHandler}
            onBrushChange={onBrushChange}
            onLineTypeChange={onLineTypeChangeHandler}
            onShapeChange={onPainterChangeHandler}
            onFillTypeChange={onFillTypeChangeHandler}
            shapeFillTypeValue={fillType}
          />
        }
        {panelState === PanelState.Highlighter &&
          <PainterPanel.Pen
            toolType={painterToolType}
            painterTool={painterTool[painterToolType]}
            onPanelClose={panelCloseHandler}
            onLineWidthChange={onLineWidthChange}
            onColorChange={onColorChange}
            onPainterHandler={onPainterChangeHandler}
            onBrushChange={onBrushChange}
            onLineTypeChange={onLineTypeChangeHandler}
            onShapeChange={onPainterChangeHandler}
            onFillTypeChange={onFillTypeChangeHandler}
            shapeFillTypeValue={fillType}
          />}
        {panelState === PanelState.Shape &&
          <PainterPanel.Shape
            toolType={painterToolType}
            painterTool={painterTool[painterToolType]}
            onPanelClose={panelCloseHandler}
            onLineWidthChange={onLineWidthChange}
            onColorChange={onColorChange}
            onShapeChange={onPainterChangeHandler}
            onFillTypeChange={onFillTypeChangeHandler}
            shapeFillTypeValue={fillType}
            onLineTypeChange={onLineTypeChangeHandler}
          />}
        {panelState === PanelState.Line &&
          <PainterPanel.Line
            toolType={painterToolType}
            painterTool={painterTool[painterToolType]}
            onPanelClose={panelCloseHandler}
            onLineWidthChange={onLineWidthChange}
            onColorChange={onColorChange}
            onLineTypeChange={onLineTypeChangeHandler}
            onPainterHandler={onPainterChangeHandler}
          />}
        {panelState === PanelState.Expression &&
          <PainterPanel.Expression
            onPanelClose={panelCloseHandler}
            onExpressionHandler={onExpressionHandler}
          />}

        { panelState === PanelState.MeasureTools && 
          <MeasureToolsPanel
            onPanelClose={panelCloseHandler}>
              {/* <ReaderToolComponents
                type="MeasureTools"
                className={styles.measureTools}
                dragableClassName={styles.middleTools}
                bookInfo={{ book, LRFlip }}
                panelState={PanelState}
                setPanelStateHandler={setPanelState}
                filter={arr => measureToolsFilter(arr)}
                dropSelf={false}
                dragable={false}
              /> */}
          </MeasureToolsPanel>}

        {isToolBoxShow &&
          <ReaderToolBox
            onClose={toolBoxCloseHandler}
          />}
        <div className={styles.left} style={role === Roles.INTERACTIVITY_STUDENT ? { width: '24%' } : {}}>
          {strategy && strategy.getReaderToolComponents(
            ReaderToolsFactory.batchCreateReaderTools(leftReaderToolBars)
          ).map(component => component)}
          {
            role === Roles.INTERACTIVITY_STUDENT && <div className={styles.studentName}>
              <img src={studentNameIcon} alt=''  />
              <div className={styles.text}>
                <div>第 { ('0' + studentInfo.studentGroup).slice(-2)} 組</div>
                <div>{`${('0' + studentInfo.studentNum).slice(-2)} ${studentInfo.studentName}`}</div>
              </div>
            </div>
          }
        </div>
        <ReaderToolComponents
          type="ReaderTool"
          className={styles.middle}
          dragableClassName={styles.middleTools}
          bookInfo={{ book, LRFlip }}
          panelState={PanelState}
          setPanelStateHandler={setPanelState}
          filter={arr => readerToolsFilter(arr)}
          dropSelf={true}
          dragable={isReaderToolboxShow}
          nowPanelState={panelState}
        />
        <div className={styles.right}>
          {strategy && strategy.getReaderToolComponents(
            ReaderToolsFactory.batchCreateReaderTools(rightReaderToolBars, {
              [ReaderToolType.OfflineReaderToolBox]: { additionalClickHandler: () => { setToolBoxShow(!isToolBoxShow); } },
              [ReaderToolType.SubmitNote]: { additionalClickHandler: submitNoteHandler }
            })
          ).map(component => component)}
        </div>
      </div>
      {
        isReaderToolboxShow && (
          <Toolbox
            toolboxType={toolboxType}
            onChange={toolboxTypeChangeHandler}
            onResetClick={resetClickHandler}
          >
            {
              ReaderToolbox[toolboxType].length > 0 && (
                <ReaderToolComponents
                  type="Toolbox"
                  isCollected={true}
                  className={styles.toolbox}
                  dragableClassName={styles.tools}
                  bookInfo={{ book, LRFlip }}
                  panelState={PanelState}
                  setPanelStateHandler={setPanelState}
                  filter={arr => toolboxFilter(arr)}
                  dropSelf={false}
                  dragable={isReaderToolboxShow}
                  nowPanelState={panelState}
                />
              )
            }
          </Toolbox>
        )
      }
    </div>
      <WarnDialog
        open={openWarnDialog}
        title="要將筆記回傳嗎？"
        subTitle={
          <div>
            請確認每一頁都已作答完畢，再進行回傳。
          </div>
        }
        handleCancel={() => setOpenWarnDialog(false)}
        handleConfirm={() => {
          return interactivityApi[ExtendedContentType.PaiNote].updateAnswer({
            teacherId,
            noteId,
            studentId: studentInfo.studentId,
            notes: paiNoteStudentAnswers
          }).then(res => {
            console.log('paiNote', res)
            return res;
          }).catch(err => console.log('派筆記學生端錯誤 err', err))
          .finally(() => {
            setOpenWarnDialog(false);
          })
        }}
      />
  </>
  );
};

export default ReaderTools;
